import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useTheme, themes } from '../ThemeProvider';
import { BACKEND_CONFIG } from '../../utils/productionConfig';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'motion/react';
import { 
  Crown, Check, Star, Sparkles, Tag, TrendingUp, 
  Calendar, CreditCard, CheckCircle, AlertCircle, 
  Unlock, X, Loader2, MessageCircle, Lock, Eye, Heart, 
  Zap, ShoppingCart
} from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import { logger } from '../../utils/logger';

const API_URL = BACKEND_CONFIG.apiUrl;

// Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface Plan {
  _id: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  duration: number;
  finalPrice: number;
  discount: number;
  isPopular: boolean;
  displayOrder?: number;
  features: {
    unlimitedMatches: boolean;
    contactDetails: boolean;
    chatUnlimited: boolean;
    prioritySupport: boolean;
    profileHighlight: boolean;
    advancedFilters: boolean;
    horoscopeMatching: boolean;
    dedicatedRM: boolean;
    videoCall: boolean;
    maxContacts: number;
    maxChats: number;
    profileBoost?: {
      count: number;
      daysPerBoost: number;
    };
  };
  customFeatures?: string[];
}

interface Order {
  _id: string;
  planId: any;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  paymentStatus: string;
  status: string;
  createdAt: string;
  subscription: {
    startDate: string;
    endDate: string;
  };
  couponId?: any;
}

interface Coupon {
  code: string;
  discountPercentage: number;
  validUntil: string;
}

export function PlansTabComplete() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [paymentHistory, setPaymentHistory] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [gstSettings, setGstSettings] = useState<any>(null);
  const [addonPlans, setAddonPlans] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [availableCoupons, setAvailableCoupons] = useState<any[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const { user } = useAuthContext();

  useEffect(() => {
    if (!dataFetched) {
      fetchData();
      fetchGSTSettings();
    }
  }, [dataFetched]);

  // Auto-fill referral code when payment dialog opens and fetch available coupons
  useEffect(() => {
    if (showPaymentDialog && selectedPlan) {
      fetchAvailableCoupons();
    }
  }, [showPaymentDialog, selectedPlan]);

  const fetchAvailableCoupons = async () => {
    if (!selectedPlan) return;
    
    try {
      setLoadingCoupons(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/coupons/available/${selectedPlan._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const coupons = response.data.data || [];
        logger.log('📋 Available coupons from API:', coupons);
        logger.log('👤 User data:', { referredBy: user?.referredBy, referralCode: user?.referralCode });
        logger.log('📊 Payment history length:', paymentHistory.length);
        
        // Add referral code if user has one and hasn't made first purchase
        if (user?.referredBy && paymentHistory.length === 0) {
          logger.log('✅ User has referredBy code and no payment history, adding to list');
          // Check if referral code is already in the list
          const hasReferralCode = coupons.some((c: any) => c.code === user.referredBy);
          if (!hasReferralCode) {
            coupons.unshift({
              code: user.referredBy,
              type: 'referral',
              discountPercentage: 10, // Will be validated from backend
              description: 'Your Referral Code - First Purchase Only',
              isReferral: true
            });
            logger.log('✅ Added referredBy code to coupons:', user.referredBy);
          } else {
            logger.log('ℹ️ ReferredBy code already in list');
          }
        } else {
          logger.log('⚠️ Not adding referredBy code. Reasons:', {
            hasReferredBy: !!user?.referredBy,
            paymentHistoryEmpty: paymentHistory.length === 0
          });
        }
        
        // Filter out user's own referral code from the list
        const filteredCoupons = coupons.filter((c: any) => {
          // Don't show user's own referral code
          if (user?.referralCode && c.code === user.referralCode) {
            logger.log('🚫 Filtering out user own referral code:', user.referralCode);
            return false;
          }
          return true;
        });
        
        logger.log('📋 Final filtered coupons:', filteredCoupons);
        setAvailableCoupons(filteredCoupons);
      }
    } catch (error) {
      logger.error('Error fetching coupons:', error);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const fetchGSTSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/gst-settings`);
      setGstSettings(data.data);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Fetch all plans
      const plansRes = await axios.get(`${API_URL}/plans`);
      if (plansRes.data.success) {
        setPlans(plansRes.data.data.plans);
      }

      // Fetch settings to check if addon plans are enabled
      try {
        const settingsRes = await axios.get(`${API_URL}/settings`);
        if (settingsRes.data.success) {
          setSettings(settingsRes.data.settings);
          
          // Fetch addon plans if enabled
          if (settingsRes.data.settings?.features?.addonPlansEnabled) {
            const addonRes = await axios.get(`${API_URL}/premium-plans`);
            if (addonRes.data.success) {
              // Filter only addon plans (where addonType is not null)
              const addons = (addonRes.data.data || []).filter((plan: any) => 
                plan.addonType && plan.isActive
              );
              setAddonPlans(addons);
            }
          }
        }
      } catch (err) {
        // Settings not available
      }

      // Fetch current subscription
      try {
        const subRes = await axios.get(`${API_URL}/user/subscription`);
        logger.log('🔍 [PlansTabComplete] Subscription API response:', subRes.data);
        if (subRes.data.success && subRes.data.data) {
          logger.log('🔍 [PlansTabComplete] Setting currentSubscription:', subRes.data.data);
          logger.log('🔍 [PlansTabComplete] hasSubscription:', subRes.data.data.hasSubscription);
          logger.log('🔍 [PlansTabComplete] planDetails:', subRes.data.data.planDetails);
          setCurrentSubscription(subRes.data.data);
        }
      } catch (err) {
        logger.error('❌ [PlansTabComplete] Error fetching subscription:', err);
        // No subscription
      }

      // Fetch payment history
      try {
        const historyRes = await axios.get(`${API_URL}/payments/orders`);
        if (historyRes.data.success) {
          setPaymentHistory(historyRes.data.data.orders || []);
        }
      } catch (err) {
        // No payment history
      }

      setLoading(false);
      setDataFetched(true);
    } catch (error: any) {
      setLoading(false);
    }
  };

  const validateCoupon = async () => {
    if (!couponCode || !selectedPlan) return;

    try {
      setValidatingCoupon(true);
      const res = await axios.post(`${API_URL}/coupons/validate`, {
        code: couponCode,
        planId: selectedPlan._id
      });

      if (res.data.success) {
        setAppliedCoupon(res.data.data.coupon);
        toast.success(`Coupon applied! ${res.data.data.coupon.discountPercentage}% discount`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Invalid coupon');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
  };

  const applyAvailableCoupon = async (couponToApply: any) => {
    setCouponCode(couponToApply.code);
    
    if (!selectedPlan) return;

    try {
      setValidatingCoupon(true);
      const res = await axios.post(`${API_URL}/coupons/validate`, {
        code: couponToApply.code,
        planId: selectedPlan._id
      });

      if (res.data.success) {
        setAppliedCoupon(res.data.data.coupon);
        toast.success(`${couponToApply.isReferral ? 'Referral code' : 'Coupon'} applied! ${res.data.data.coupon.discountPercentage}% discount`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Invalid coupon');
      setAppliedCoupon(null);
      setCouponCode('');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const calculateFinalPrice = () => {
    if (!selectedPlan) return 0;
    
    let price = selectedPlan.finalPrice;
    
    if (appliedCoupon) {
      const discount = (price * appliedCoupon.discountPercentage) / 100;
      price = price - discount;
    }
    
    return Math.round(price);
  };

  const handleBuyPlan = (plan: Plan) => {
    setSelectedPlan(plan);
    setCouponCode('');
    setAppliedCoupon(null);
    setShowPaymentDialog(true);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!selectedPlan) return;

    try {
      setProcessingPayment(true);

      // Create order
      const orderRes = await axios.post(`${API_URL}/payments/create-order`, {
        planId: selectedPlan._id,
        couponCode: appliedCoupon?.code || undefined,
        paymentMethod: 'razorpay'
        // Backend will calculate GST-inclusive amount
      });

      if (!orderRes.data.success) {
        throw new Error(orderRes.data.message || 'Failed to create order');
      }

      const { order, razorpayOrder, razorpayKeyId, isFree } = orderRes.data.data;

      // If it's a free order (100% discount), skip Razorpay
      if (isFree) {
        setShowPaymentDialog(false);
        setShowSuccessDialog(true);
        toast.success('🎉 Plan activated successfully!');
        
        // Refresh data
        setTimeout(() => {
          fetchData();
        }, 1000);
        setProcessingPayment(false);
        return;
      }

      if (!razorpayKeyId) {
        toast.error('Payment gateway not configured properly');
        setProcessingPayment(false);
        return;
      }

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setProcessingPayment(false);
        return;
      }

      // Razorpay options
      const options = {
        key: razorpayKeyId,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: 'Matrimonial Platform',
        description: `${selectedPlan.name} Plan - ${selectedPlan.duration} months`,
        order_id: razorpayOrder.id,
        handler: async function (response: any) {
          try {
            // Verify payment
            const verifyRes = await axios.post(`${API_URL}/payments/verify`, {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature
            });

            if (verifyRes.data.success) {
              setShowPaymentDialog(false);
              setShowSuccessDialog(true);
              toast.success('Payment successful! Plan activated');
              
              // Poll for subscription activation with retries
              let retries = 0;
              const maxRetries = 10;
              const pollInterval = 2000; // 2 seconds
              
              const checkSubscription = async () => {
                try {
                  const subRes = await axios.get(`${API_URL}/user/subscription`);
                  if (subRes.data.success && subRes.data.data?.subscription?.isActive) {
                    fetchData();
                    return true;
                  }
                  return false;
                } catch (err) {
                  return false;
                }
              };
              
              const pollForActivation = async () => {
                const isActive = await checkSubscription();
                if (isActive) {
                  return;
                }
                
                retries++;
                if (retries < maxRetries) {
                  setTimeout(pollForActivation, pollInterval);
                } else {
                  // Final refresh after max retries
                  fetchData();
                }
              };
              
              // Start polling after 1 second
              setTimeout(pollForActivation, 1000);
            }
          } catch (error: any) {
            toast.error(error.response?.data?.message || 'Payment verification failed');
          }
        },
        prefill: {
          name: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '',
          email: user?.email || '',
          contact: user?.phone || ''
        },
        theme: {
          color: '#f43f5e'
        },
        modal: {
          ondismiss: function() {
            setProcessingPayment(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', function (response: any) {
        toast.error('Payment failed. Please try again.');
        setProcessingPayment(false);
      });

      razorpay.open();
      // Don't reset processingPayment here - let modal.ondismiss handle it
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Payment failed');
      setProcessingPayment(false);
    }
  };

  const getDaysUntilExpiry = () => {
    if (!currentSubscription?.subscription?.endDate) return null;
    const endDate = new Date(currentSubscription.subscription.endDate);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getCurrentPlanTier = () => {
    if (!currentSubscription?.subscription?.planId) return 0;
    const currentPlanId = typeof currentSubscription.subscription.planId === 'object' 
      ? currentSubscription.subscription.planId._id 
      : currentSubscription.subscription.planId;
    const currentPlan = plans.find(p => p._id === currentPlanId);
    return currentPlan?.displayOrder || 0;
  };

  const isCurrentPlanCheck = (planId: string) => {
    if (!currentSubscription?.subscription?.planId) return false;
    const currentPlanId = typeof currentSubscription.subscription.planId === 'object' 
      ? currentSubscription.subscription.planId._id 
      : currentSubscription.subscription.planId;
    return currentPlanId === planId;
  };

  const getButtonText = (plan: Plan) => {
    if (!currentSubscription?.hasSubscription) return 'Get Started';
    
    if (isCurrentPlanCheck(plan._id)) {
      const daysLeft = getDaysUntilExpiry();
      if (daysLeft !== null && daysLeft <= 10) {
        return 'Renew Plan';
      }
      return 'Current Plan';
    }
    
    const currentTier = getCurrentPlanTier();
    if ((plan.displayOrder || 0) > currentTier) {
      return 'Upgrade Plan';
    }
    
    return 'Switch Plan';
  };

  const isButtonDisabled = (plan: Plan) => {
    if (!currentSubscription?.hasSubscription) return false;
    if (isCurrentPlanCheck(plan._id)) {
      const daysLeft = getDaysUntilExpiry();
      // Only allow renew if less than 10 days left
      return daysLeft !== null && daysLeft > 10;
    }
    return false;
  };

  const canBuyPlan = (plan: Plan) => {
    if (!currentSubscription || !currentSubscription.hasSubscription) {
      return true; // Can buy any plan if no active subscription
    }

    const currentTier = getCurrentPlanTier();
    return (plan.displayOrder || 0) >= currentTier; // Can only upgrade or same
  };

  const getPlanIcon = (index: number) => {
    const icons = [Star, Crown, Sparkles];
    return icons[index] || Crown;
  };

  const getPlanGradient = (index: number) => {
    const gradients = [
      'from-gray-500 to-gray-600',
      'from-rose-500 to-purple-600',
      'from-amber-500 to-orange-600'
    ];
    return gradients[index] || 'from-rose-500 to-purple-600';
  };

  const formatFeaturesList = (plan: Plan) => {
    const features: string[] = [];

    if (plan.features.unlimitedMatches) features.push('Unlimited Profile Views');
    if (plan.features.contactDetails) features.push('View Contact Details');
    if (plan.features.chatUnlimited) features.push('Unlimited Chat Messages');
    if (plan.features.prioritySupport) features.push('Priority Customer Support');
    if (plan.features.profileHighlight) features.push('Profile Highlighting');
    if (plan.features.advancedFilters) features.push('Advanced Search Filters');
    if (plan.features.horoscopeMatching) features.push('Horoscope Matching');
    if (plan.features.dedicatedRM) features.push('Dedicated Relationship Manager');
    if (plan.features.videoCall) features.push('Video Call Feature');
    
    if (plan.features.maxContacts > 0) {
      features.push(`${plan.features.maxContacts} Contact Views`);
    }
    
    if (plan.features.profileBoost && plan.features.profileBoost.count > 0) {
      features.push(`${plan.features.profileBoost.count} Profile Boosts`);
    }

    if (plan.customFeatures) {
      features.push(...plan.customFeatures);
    }

    return features;
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="h-10 bg-gray-200 animate-pulse rounded w-48 mx-auto" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-6 bg-white rounded-lg border shadow-lg">
              <div className="space-y-4">
                <div className="h-6 bg-gray-200 animate-pulse w-32 mx-auto rounded" />
                <div className="h-10 bg-gray-200 animate-pulse w-40 mx-auto rounded" />
                <div className="space-y-2 pt-4">
                  {[1, 2, 3, 4, 5].map((j) => (
                    <div key={j} className="flex items-center gap-2">
                      <div className="h-4 w-4 bg-gray-200 animate-pulse rounded-full" />
                      <div className="h-4 flex-1 bg-gray-200 animate-pulse rounded" />
                    </div>
                  ))}
                </div>
                <div className="h-12 bg-gray-200 animate-pulse w-full rounded-lg mt-6" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      {/* Desktop View */}
      <div className="hidden md:block">
        <div className="max-w-[1200px] mx-auto">
          {/* Page Heading */}
          <section className="w-full px-4 pt-12 pb-6 text-center lg:px-20">
            <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4">
              Invest in your future. <br className="hidden md:block" /> Select a plan that fits your journey.
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Premium members are 5x more likely to find a match within the first 30 days. Choose the plan that's right for you and start your journey today.
            </p>
          </section>

          {/* Available Plans */}
          <section className="w-full px-4 pb-16 lg:px-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start pt-6">
              {plans.map((plan, index) => {
                const Icon = getPlanIcon(index);
                const gradient = getPlanGradient(index);
                const features = formatFeaturesList(plan);
                const canBuy = canBuyPlan(plan);
                const isCurrentPlan = isCurrentPlanCheck(plan._id);
                const buttonText = getButtonText(plan);
                const disabled = isButtonDisabled(plan);
                const isPlatinum = plan.isPopular;
                const isFree = plan.finalPrice === 0;

                return (
                  <motion.div
                    key={plan._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex flex-col gap-6 rounded-2xl border bg-white p-8 transition-all duration-300 hover:shadow-xl relative ${
                      isPlatinum 
                        ? 'border-2 border-pink-600 scale-105 shadow-xl z-10' 
                        : isFree
                        ? 'border-gray-200 mt-8'
                        : 'border-gray-200 mt-4'
                    }`}
                  >
                    {/* Best Value Badge */}
                    {isPlatinum && (
                      <div className="absolute -top-4 left-0 right-0 flex justify-center">
                        <span className="bg-pink-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md uppercase tracking-wide">
                          Best Value
                        </span>
                      </div>
                    )}

                    {/* Plan Header */}
                    <div className={`flex flex-col gap-2 border-b border-gray-100 pb-6 ${isPlatinum ? 'pt-2' : ''}`}>
                      <div className="flex items-center justify-between">
                        <h2 className={`font-bold ${isPlatinum ? 'text-2xl' : 'text-xl'}`}>
                          {plan.name}
                        </h2>
                        {!isPlatinum && !isFree && index === 1 && (
                          <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">
                            Popular
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm">{plan.description}</p>
                      
                      <div className="mt-4 flex items-baseline gap-1">
                        <span className={`font-black tracking-tight ${isPlatinum ? 'text-5xl text-pink-600' : 'text-4xl'}`}>
                          ₹{plan.finalPrice || plan.price}
                        </span>
                        <span className="text-base font-medium text-gray-600">
                          {isFree ? '/forever' : `/${plan.duration} days`}
                        </span>
                      </div>
                      
                      {plan.discount > 0 && (
                        <p className="text-xs text-green-600 font-medium">
                          Valid for {plan.duration} days
                        </p>
                      )}
                    </div>

                    {/* Features List */}
                    <div className="flex flex-col gap-4 flex-1">
                      {features.slice(0, 5).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-sm">
                          {isPlatinum ? (
                            <svg className="h-5 w-5 text-pink-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          ) : isFree ? (
                            <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                          ) : (
                            <Check className="h-5 w-5 text-pink-600 flex-shrink-0" />
                          )}
                          <span className={idx < 2 ? 'font-semibold' : ''}>{feature}</span>
                        </div>
                      ))}
                      {features.length > 5 && features.slice(5).map((feature, idx) => (
                        <div key={idx + 5} className="flex items-center gap-3 text-sm text-gray-400">
                          <X className="h-5 w-5 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <Button
                      className={`w-full h-12 rounded-xl text-sm font-bold shadow-md transition-all ${
                        isPlatinum
                          ? 'bg-pink-600 hover:bg-pink-700 text-white shadow-lg shadow-pink-200 hover:scale-[1.02]'
                          : isFree
                          ? 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                          : index === 1
                          ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200'
                          : 'bg-pink-600 hover:bg-pink-700 text-white'
                      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => handleBuyPlan(plan)}
                      disabled={disabled}
                    >
                      {disabled && buttonText === 'Current Plan' ? (
                        <><CheckCircle className="h-4 w-4 mr-2" />{buttonText}</>
                      ) : isPlatinum ? (
                        <>Go Platinum</>
                      ) : isFree ? (
                        <>Continue for Free</>
                      ) : index === 1 ? (
                        <>Select Gold</>
                      ) : (
                        <><Unlock className="h-4 w-4 mr-2" />{buttonText}</>
                      )}
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* Addon Plans Section */}
          {settings?.features?.addonPlansEnabled && addonPlans.length > 0 && (
            <section className="w-full px-4 pb-16 lg:px-10">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2">Addon Plans</h2>
                <p className="text-gray-600">
                  Enhance your experience with additional credits and features
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                {addonPlans.map((addon, index) => {
                  const getAddonIcon = () => {
                    switch (addon.addonType) {
                      case 'contact_views': return Eye;
                      case 'super_interests': return Star;
                      case 'interests': return Heart;
                      case 'video_calls': return MessageCircle;
                      case 'profile_booster': return TrendingUp;
                      default: return Zap;
                    }
                  };

                  const Icon = getAddonIcon();
                  const requiresPlan = addon.requiresActivePlan && !currentSubscription?.hasSubscription;

                  return (
                    <motion.div
                      key={addon._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="relative bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl transition-all"
                    >
                      {/* Addon Badge */}
                      <div className="absolute -top-3 left-6">
                        <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 text-xs px-3 py-1">
                          ADDON
                        </Badge>
                      </div>

                      {/* Icon */}
                      <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${addon.color} mb-4 mt-2`}>
                        <Icon className="size-8 text-white" />
                      </div>

                      {/* Plan Info */}
                      <h3 className="text-xl font-bold mb-2">{addon.name}</h3>
                      <p className="text-gray-600 text-sm mb-4">{addon.description}</p>

                      {/* Price */}
                      <div className="flex items-baseline gap-2 mb-4">
                        <span className="text-3xl font-bold">₹{addon.price}</span>
                        <span className="text-gray-500 text-sm">
                          / {addon.creditsValue || addon.credits} credits
                        </span>
                      </div>

                      {/* Features */}
                      <div className="space-y-2 mb-6">
                        {addon.features?.slice(0, 3).map((feature: string, idx: number) => (
                          <div key={idx} className="flex items-start gap-2">
                            <CheckCircle className="size-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                        {addon.features?.length > 3 && (
                          <p className="text-xs text-gray-500 ml-6">+{addon.features.length - 3} more features</p>
                        )}
                      </div>

                      {/* Requires Plan Warning */}
                      {requiresPlan && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                          <Lock className="size-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-amber-700">
                            Requires active subscription to purchase
                          </p>
                        </div>
                      )}

                      {/* Buy Button */}
                      <Button
                        onClick={() => handleBuyPlan({ ...addon, finalPrice: addon.price } as any)}
                        disabled={requiresPlan}
                        className={`w-full ${
                          requiresPlan 
                            ? 'bg-gray-300 cursor-not-allowed' 
                            : `bg-gradient-to-r ${addon.color}`
                        } text-white border-0`}
                      >
                        {requiresPlan ? (
                          <>
                            <Lock className="size-4 mr-2" />
                            Requires Plan
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="size-4 mr-2" />
                            Buy Now
                          </>
                        )}
                      </Button>
                    </motion.div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Feature Highlights Section */}
          <section className="w-full bg-white py-16">
            <div className="max-w-[960px] mx-auto px-4 flex flex-col gap-12">
              <div className="text-center flex flex-col gap-3">
                <h2 className="text-3xl font-black tracking-tight">
                  Why Upgrade?
                </h2>
                <p className="text-gray-600 text-base max-w-[600px] mx-auto">
                  Unlock exclusive features designed to speed up your partner search and ensure you meet verified people.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Feature 1 */}
                <div className="flex flex-col gap-4 rounded-xl border border-gray-100 bg-gray-50 p-6 transition-transform hover:-translate-y-1">
                  <div className="h-12 w-12 rounded-lg bg-pink-100 flex items-center justify-center text-pink-600 mb-2">
                    <MessageCircle className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold">Connect Directly</h3>
                  <p className="text-gray-600 text-sm">Chat instantly with matches you like without waiting. No more waiting for mutual matches.</p>
                </div>
                {/* Feature 2 */}
                <div className="flex flex-col gap-4 rounded-xl border border-gray-100 bg-gray-50 p-6 transition-transform hover:-translate-y-1">
                  <div className="h-12 w-12 rounded-lg bg-pink-100 flex items-center justify-center text-pink-600 mb-2">
                    <CheckCircle className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold">Verified Profiles</h3>
                  <p className="text-gray-600 text-sm">Access a database of 100% mobile-verified profiles. We ensure strict verification.</p>
                </div>
                {/* Feature 3 */}
                <div className="flex flex-col gap-4 rounded-xl border border-gray-100 bg-gray-50 p-6 transition-transform hover:-translate-y-1">
                  <div className="h-12 w-12 rounded-lg bg-pink-100 flex items-center justify-center text-pink-600 mb-2">
                    <Lock className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold">Safe & Secure</h3>
                  <p className="text-gray-600 text-sm">Your privacy is our priority with best-in-class security encryption and privacy controls.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Current Subscription & History - Tabs */}
          {(currentSubscription?.hasSubscription || paymentHistory.length > 0) && (
            <section className="w-full px-4 py-8 max-w-[1200px] mx-auto">
              <Tabs defaultValue="current" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="current">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    My Subscription
                  </TabsTrigger>
                  <TabsTrigger value="history">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Payment History
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="current">
                  {(() => {
                    logger.log('🔍 [Render] currentSubscription:', currentSubscription);
                    logger.log('🔍 [Render] hasSubscription:', currentSubscription?.hasSubscription);
                    logger.log('🔍 [Render] planDetails:', currentSubscription?.planDetails);
                    logger.log('🔍 [Render] Should show active:', currentSubscription?.hasSubscription && currentSubscription.planDetails);
                    return currentSubscription?.hasSubscription && currentSubscription.planDetails;
                  })() ? (
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-xl font-bold mb-2">{currentSubscription.planDetails.name} Plan</h3>
                            <p className="text-gray-600 mb-4">{currentSubscription.planDetails.description}</p>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span className="text-gray-600">
                                  Valid until {new Date(currentSubscription.endDate).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span className="text-green-600 font-medium">Active Subscription</span>
                              </div>
                            </div>
                          </div>
                          <Badge className="bg-green-100 text-green-800 border-0">Active</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardContent className="p-12 text-center">
                        <Crown className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-bold mb-2">No Active Subscription</h3>
                        <p className="text-gray-600 mb-4">Upgrade to a premium plan to unlock all features</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="history">
                  {paymentHistory.length > 0 ? (
                    <Card>
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          {paymentHistory.map((order) => (
                            <div key={order._id} className="flex items-center justify-between p-4 border rounded-lg">
                              <div>
                                <h4 className="font-semibold">{order.planId?.name || 'Plan'}</h4>
                                <p className="text-sm text-gray-600">
                                  {new Date(order.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold">₹{order.finalAmount}</p>
                                <Badge className={order.paymentStatus === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                                  {order.paymentStatus}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardContent className="p-12 text-center">
                        <CreditCard className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-bold mb-2">No Payment History</h3>
                        <p className="text-gray-600">Your transactions will appear here</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            </section>
          )}
        </div>
      </div>

      {/* Mobile View */}
      <div className="md:hidden p-4 space-y-6">
        <Tabs defaultValue="plans" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4 h-auto">
            <TabsTrigger value="plans" className="flex flex-col items-center gap-1 py-2 px-2 text-xs">
              <Crown className="h-4 w-4" />
              <span>Plans</span>
            </TabsTrigger>
            <TabsTrigger value="current" className="flex flex-col items-center gap-1 py-2 px-2 text-xs">
              <CheckCircle className="h-4 w-4" />
              <span>Active</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex flex-col items-center gap-1 py-2 px-2 text-xs">
              <CreditCard className="h-4 w-4" />
              <span>History</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="plans" className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-xl font-bold mb-2">Choose Your Plan</h2>
              <p className="text-sm text-gray-600">Upgrade to unlock premium features and find your perfect match</p>
            </motion.div>

            <div className="space-y-4">
              {plans.map((plan, index) => {
                const Icon = getPlanIcon(index);
                const gradient = getPlanGradient(index);
                const features = formatFeaturesList(plan);
                const canBuy = canBuyPlan(plan);
                const isCurrentPlan = isCurrentPlanCheck(plan._id);
                const buttonText = getButtonText(plan);
                const disabled = isButtonDisabled(plan);

                return (
                  <motion.div
                    key={plan._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`relative border-0 shadow-xl transition-all duration-300 ${
                      plan.isPopular ? 'ring-2 ring-rose-500 ring-offset-2' : ''
                    } ${isCurrentPlan ? 'ring-2 ring-green-500 ring-offset-2' : ''}`}>
                      {plan.isPopular && (
                        <div className="absolute top-0 right-0 z-10">
                          <div className="bg-gradient-to-r from-rose-500 to-purple-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                            Most Popular
                          </div>
                        </div>
                      )}
                      
                      {isCurrentPlan && (
                        <div className="absolute top-0 left-0 z-10">
                          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-1 text-sm font-semibold rounded-br-lg">
                            Current Plan
                          </div>
                        </div>
                      )}

                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 text-center">
                        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${gradient} mb-4`}>
                          <Icon className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                        <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                        
                        {plan.discount > 0 && (
                          <div className="flex items-center justify-center space-x-2 mb-2">
                            <span className="text-2xl line-through text-gray-400">₹{plan.price}</span>
                            <Badge className="bg-red-500 text-white border-0">
                              {plan.discount}% OFF
                            </Badge>
                          </div>
                        )}
                        <div>
                          <span className="text-4xl font-bold">₹{plan.finalPrice}</span>
                          <span className="text-gray-600 ml-2">/ {plan.duration} days</span>
                        </div>
                      </div>

                      <CardContent className="p-6">
                        <ul className="space-y-3 mb-6">
                          {features.map((feature, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r ${gradient} flex items-center justify-center mt-0.5`}>
                                <Check className="h-3 w-3 text-white" />
                              </div>
                              <span className="text-sm text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <Button
                          className={`w-full ${disabled ? 'bg-gray-400 cursor-not-allowed' : `bg-gradient-to-r ${gradient}`} border-0 hover:opacity-90`}
                          onClick={() => handleBuyPlan(plan)}
                          disabled={disabled}
                        >
                          {disabled && buttonText === 'Current Plan' ? (
                            <><CheckCircle className="h-4 w-4 mr-2" />{buttonText}</>
                          ) : (
                            <><Unlock className="h-4 w-4 mr-2" />{buttonText}</>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>

          {/* Current Subscription Tab */}
          <TabsContent value="current" className="space-y-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              {currentSubscription && currentSubscription.hasSubscription && currentSubscription.subscription ? (
                <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-50 to-rose-50">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-xl font-bold">Active Subscription</h3>
                          <Badge className="bg-gradient-to-r from-amber-500 to-rose-500 text-white border-0">
                            {currentSubscription.subscription.planId?.name || 'Premium'}
                          </Badge>
                        </div>
                        <p className="text-gray-600">
                          Valid until {new Date(currentSubscription.subscription.endDate).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                      <div className="bg-gradient-to-r from-amber-500 to-rose-500 rounded-full p-3">
                        <Crown className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                      <div className="bg-white rounded-lg p-4 text-center">
                        <Calendar className="h-6 w-6 text-rose-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Started</p>
                        <p className="font-semibold">{new Date(currentSubscription.subscription.startDate).toLocaleDateString('en-IN')}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 text-center">
                        <Calendar className="h-6 w-6 text-rose-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Expires</p>
                        <p className="font-semibold">{new Date(currentSubscription.subscription.endDate).toLocaleDateString('en-IN')}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 text-center">
                        <TrendingUp className="h-6 w-6 text-rose-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Days Left</p>
                        <p className="font-semibold">
                          {Math.ceil((new Date(currentSubscription.subscription.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 text-center">
                        <CheckCircle className="h-6 w-6 text-green-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Status</p>
                        <p className="font-semibold text-green-600">Active</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-12 text-center">
                    <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">No Active Subscription</h3>
                    <p className="text-gray-600 mb-6">Upgrade to premium to unlock all features</p>
                    <Button
                      className="bg-gradient-to-r from-rose-500 to-purple-600 border-0"
                      onClick={() => {
                        const tabs = document.querySelector('[value="plans"]') as HTMLElement;
                        tabs?.click();
                      }}
                    >
                      Browse Plans
                    </Button>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </TabsContent>

          {/* Payment History Tab */}
          <TabsContent value="history" className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-2xl font-bold mb-2">Payment History</h2>
              <p className="text-gray-600">View all your past transactions</p>
            </motion.div>

            {paymentHistory.length > 0 ? (
              <div className="space-y-3">
                {paymentHistory.map((payment, index) => (
                  <motion.div
                    key={payment._id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="border-0 shadow hover:shadow-lg transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className={`bg-gradient-to-r ${currentTheme.primary} rounded-full p-3`}>
                              <CreditCard className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                {payment.planId?.name || 'Premium Plan'}
                              </h4>
                              <div className="flex items-center space-x-4 text-sm text-gray-600">
                                <div className="flex items-center space-x-1">
                                  <Calendar className="h-3 w-3" />
                                  <span>{new Date(payment.createdAt).toLocaleDateString('en-IN')}</span>
                                </div>
                                {payment.couponId && (
                                  <div className="flex items-center space-x-1">
                                    <Tag className="h-3 w-3" />
                                    <span>Coupon Applied</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-lg text-gray-900">₹{payment.finalAmount}</div>
                            {payment.discountAmount > 0 && (
                              <div className="text-xs text-gray-500 line-through">₹{payment.originalAmount}</div>
                            )}
                            <Badge className={
                              payment.paymentStatus === 'completed' 
                                ? 'bg-green-100 text-green-700 border-0 text-xs mt-1' 
                                : 'bg-yellow-100 text-yellow-700 border-0 text-xs mt-1'
                            }>
                              {payment.paymentStatus || 'Completed'}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <CreditCard className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">No Payment History</h3>
                  <p className="text-gray-600">Your payment history will appear here</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl">Complete Your Purchase</DialogTitle>
          </DialogHeader>

          {selectedPlan && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">{selectedPlan.name} Plan</span>
                  <span className="font-bold">₹{selectedPlan.finalPrice}</span>
                </div>
                <p className="text-sm text-gray-600">Valid for {selectedPlan.duration} days</p>
              </div>

              {/* Coupon Section */}
              <div>
                <label className="text-sm font-semibold mb-2 block">Have a coupon code?</label>
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <Tag className="h-4 w-4 text-green-600" />
                      <div>
                        <span className="font-semibold text-green-700">{appliedCoupon.code}</span>
                        <p className="text-xs text-green-600">{appliedCoupon.discountPercentage}% discount applied</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={removeCoupon}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex space-x-2 mb-3">
                      <Input
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="flex-1"
                      />
                      <Button
                        onClick={validateCoupon}
                        disabled={!couponCode || validatingCoupon}
                        className="bg-gradient-to-r from-rose-500 to-purple-600 border-0"
                      >
                        {validatingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>

                    {/* Available Coupons List */}
                    {loadingCoupons ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                        <span className="ml-2 text-sm text-gray-500">Loading coupons...</span>
                      </div>
                    ) : availableCoupons.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500 mb-2">Available Coupons:</p>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {availableCoupons.map((coupon, index) => (
                            <div
                              key={index}
                              className={`border rounded-lg p-3 cursor-pointer transition-all hover:border-rose-300 hover:bg-rose-50 ${
                                coupon.isReferral ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'
                              }`}
                              onClick={() => applyAvailableCoupon(coupon)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    {coupon.isReferral ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-600 text-white">
                                        Referral Code
                                      </span>
                                    ) : (
                                      <Tag className="h-3 w-3 text-rose-500" />
                                    )}
                                    <span className="font-bold text-sm">{coupon.code}</span>
                                  </div>
                                  <p className="text-xs text-gray-600">{coupon.description}</p>
                                  {coupon.isReferral && (
                                    <p className="text-xs text-green-700 font-semibold mt-1">
                                      ✨ First Purchase Only
                                    </p>
                                  )}
                                </div>
                                <div className="text-right ml-3">
                                  <span className="text-rose-600 font-bold text-lg">{coupon.discountPercentage}%</span>
                                  <p className="text-xs text-gray-500">OFF</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 text-center py-2">No coupons available for this plan</p>
                    )}
                  </>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Plan Price</span>
                  <span>₹{selectedPlan.finalPrice}</span>
                </div>
                {appliedCoupon && (
                  <div className="flex items-center justify-between text-sm text-green-600">
                    <span>Coupon Discount ({appliedCoupon.discountPercentage}%)</span>
                    <span>- ₹{Math.round((selectedPlan.finalPrice * appliedCoupon.discountPercentage) / 100)}</span>
                  </div>
                )}
                {gstSettings?.isEnabled && (
                  <div className="flex items-center justify-between text-sm text-green-700">
                    <span>GST ({gstSettings.gstPercentage}%)</span>
                    <span>+ ₹{((calculateFinalPrice() * gstSettings.gstPercentage) / 100).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between font-bold text-lg border-t pt-2">
                  <span>Total Amount</span>
                  <span className="text-rose-600">₹{
                    gstSettings?.isEnabled 
                      ? (calculateFinalPrice() + (calculateFinalPrice() * gstSettings.gstPercentage) / 100).toFixed(2)
                      : calculateFinalPrice()
                  }</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              disabled={processingPayment}
              className="bg-gradient-to-r from-rose-500 to-purple-600 border-0"
            >
              {processingPayment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay ₹{
                    gstSettings?.isEnabled 
                      ? (calculateFinalPrice() + (calculateFinalPrice() * gstSettings.gstPercentage) / 100).toFixed(2)
                      : calculateFinalPrice()
                  }
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Payment Successful!</h3>
            <p className="text-gray-600 mb-6">
              Your {selectedPlan?.name} plan has been activated successfully
            </p>
            <Button
              onClick={() => {
                setShowSuccessDialog(false);
                setSelectedPlan(null);
              }}
              className="bg-gradient-to-r from-rose-500 to-purple-600 border-0"
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}